import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, Button, Input, Label, Skeleton } from '@monksflow/monks-ui';
import { TriangleAlertIcon } from 'lucide-react';

import {
  trackerAiBudgetQueryOptions,
  useUpdateTrackerAiBudgetMutation,
  type TrackerAiBudget,
} from '@/entities/tracker-automation';
import { usePermissions } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';

import { automationErrorKey } from '../../lib/automation-errors';

const MINOR_PER_MAJOR = 100;

/** The server's own ceiling, USD 100,000.00, so a typo is caught before it is sent. */
const MAX_CAP_MINOR = 10_000_000;

/** The organization's monthly AI budget, which only its admins may change. */
export function AutomationAiBudget() {
  const { t } = useTranslation('trackers');
  const { isOrganizationAdmin } = usePermissions();
  const { data: budget, isPending, isError } = useQuery(trackerAiBudgetQueryOptions());

  if (isPending) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        <Skeleton className="h-4 w-2/3 rounded-sm" />
        <Skeleton className="h-9 w-40 rounded-md" />
      </div>
    );
  }
  if (isError) {
    return (
      <Alert role="alert">
        <TriangleAlertIcon aria-hidden />
        <AlertDescription>{t('trackersBoard.automationAi.budget.loadFailed')}</AlertDescription>
      </Alert>
    );
  }
  return (
    <BudgetForm key={budget.monthlyCap.amountMinor} budget={budget} canEdit={isOrganizationAdmin} />
  );
}

function BudgetForm({ budget, canEdit }: { budget: TrackerAiBudget; canEdit: boolean }) {
  const { t, i18n } = useTranslation('trackers');
  const update = useUpdateTrackerAiBudgetMutation();
  const [draft, setDraft] = useState(() => String(budget.monthlyCap.amountMinor / MINOR_PER_MAJOR));
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: budget.monthlyCap.currency,
  });
  const amountMinor = Math.round(Number(draft) * MINOR_PER_MAJOR);
  const tooHigh = Number.isFinite(amountMinor) && amountMinor > MAX_CAP_MINOR;
  const valid = draft.trim() !== '' && Number.isFinite(amountMinor) && amountMinor >= 0 && !tooHigh;
  const resets = new Date(budget.periodEndsAt).toLocaleDateString(i18n.language, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
  });

  const save = () => {
    update.mutate(
      { amountMinor },
      {
        onSuccess: () =>
          notificationService.showSuccess(t('trackersBoard.automationAi.budget.saved')),
        onError: (error) =>
          notificationService.showError(
            t(automationErrorKey(error, 'trackersBoard.automationAi.budget.saveFailed')),
          ),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        {t('trackersBoard.automationAi.budget.intro')}
      </p>
      <p className="text-sm text-foreground" role="status">
        {t('trackersBoard.automationAi.budget.spent', {
          spent: money.format(budget.monthToDateSpend.amountMinor / MINOR_PER_MAJOR),
          cap: money.format(budget.monthlyCap.amountMinor / MINOR_PER_MAJOR),
        })}{' '}
        {t('trackersBoard.automationAi.budget.resets', { date: resets })}
      </p>
      {budget.isDefault ? (
        <p className="text-xs text-muted-foreground">
          {t('trackersBoard.automationAi.budget.defaultNote')}
        </p>
      ) : null}
      {canEdit ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="automation-ai-budget">
            {t('trackersBoard.automationAi.budget.capLabel', {
              currency: budget.monthlyCap.currency,
            })}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="automation-ai-budget"
              type="number"
              inputMode="decimal"
              min={0}
              max={MAX_CAP_MINOR / MINOR_PER_MAJOR}
              step={0.01}
              className="w-40"
              value={draft}
              aria-invalid={!valid}
              onChange={(event) => setDraft(event.target.value)}
            />
            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={!valid || amountMinor === budget.monthlyCap.amountMinor || update.isPending}
            >
              {t('trackersBoard.automationAi.budget.save')}
            </Button>
          </div>
          {!valid ? (
            <p className="text-xs text-destructive">
              {tooHigh
                ? t('trackersBoard.automationAi.budget.tooHigh', {
                    max: money.format(MAX_CAP_MINOR / MINOR_PER_MAJOR),
                  })
                : t('trackersBoard.automationAi.budget.invalid')}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t('trackersBoard.automationAi.budget.adminOnly')}
        </p>
      )}
    </div>
  );
}
